/**
 * Lógica pura de códigos de barras (CC-6: NUNCA usar macros lingui aquí).
 *
 * Soporta lectura por lector HID (teclado: emite caracteres + Enter) y, en la
 * UI, entrada manual. Funciona offline: la resolución es 100% local contra el
 * catálogo ya sincronizado. Sin dependencias de red ni de cámara.
 */

export interface ProductLike {
  id: string;
  sku?: string | null;
  barcode?: string | null;
  name?: string | null;
}

/**
 * Caracteres de control ASCII (0x00–0x1F y 0x7F) que un lector HID puede emitir
 * (p. ej. el Enter \r/\n al final del escaneo). Se construye con `new RegExp`
 * para no incrustar bytes de control en el fuente.
 */
const CONTROL_CHARS = new RegExp(String.raw`[\u0000-\u001F\u007F]`, "g");

/** Normaliza un código escaneado: recorta espacios y caracteres de control. */
export function normalizeBarcode(raw: string): string {
  return raw.replace(CONTROL_CHARS, "").trim();
}

/** ¿El código parece válido para buscar? (no vacío tras normalizar). */
export function isScannableCode(raw: string): boolean {
  return normalizeBarcode(raw).length > 0;
}

/**
 * Busca un producto por código de barras (coincidencia exacta) y, como respaldo,
 * por SKU. Devuelve null si no hay coincidencia. Determinista y case-sensitive
 * para barcode (los EAN/UPC son numéricos); el SKU se compara sin distinguir
 * mayúsculas para tolerar tecleo manual.
 */
export function findProductByCode<T extends ProductLike>(products: T[], rawCode: string): T | null {
  const code = normalizeBarcode(rawCode);
  if (code === "") {
    return null;
  }
  const byBarcode = products.find((p) => (p.barcode ?? "") === code);
  if (byBarcode) {
    return byBarcode;
  }
  const lowered = code.toLowerCase();
  const bySku = products.find((p) => (p.sku ?? "").toLowerCase() === lowered);
  return bySku ?? null;
}
